import classNames from "classnames";
import _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useBeforeUnload, useNavigate, useParams } from "react-router-dom";
import { EventCardSkeleton } from "../../../components/event/eventCard";
import { Icon } from "../../../components/icon/icon";
import { BareLayout } from "../../../components/layout/bareLayout";
import { Protected } from "../../../components/layout/protected";
import { ConfirmExitModal } from "../../../components/modal/app_modals/confirmExitModal";
import { ProgressBar } from "../../../components/progressBar";
import { useModalControl } from "../../../customeHooks/useModalControl";

import {
  useCreateEventMutation,
  useEditEventMutation,
  useGetEventGuestsQuery,
  useGetEventQuery,
  useGetEventTicketsQuery,
} from "../../../redux/features/event/event.api";
import { CreateEvent } from "../../../redux/features/event/event.types";
import { clientTZ } from "../../../utils/constants";
import {
  formatDate,
  getRoundedTimeToNearestXMin,
} from "../../../utils/helpers";

import { useAppSelector } from "../../../redux/hooks";
import { EventDetails } from "./eventDetails";
import { EventPreview } from "./eventPreview";
import { EventTickets } from "./eventTickets";

const steps = [
  { title: "Event Details", id: 1 },
  { title: "Tickets", id: 2 },
  { title: "Preview", id: 3 },
];

export const EventForm = ({ mode = "create" }: { mode: "create" | "edit" }) => {
  const [currentPayload, setCurrentPayload] = useState<CreateEvent>();
  const [finalPayload, setFinalPayload] = useState<CreateEvent>();
  const [step, setStep] = useState<number>(1);
  const [showLivePreview, setShowLivePreview] = useState(false);

  const { hasCompletedProfile, hasCreatedEvent } = useAppSelector(
    (state) => state.auth,
  );

  const navigate = useNavigate();

  const { slug } = useParams<{ slug: string }>();

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const [createEvent, { isLoading: creatingEvent }] = useCreateEventMutation();
  const [editEvent, { isLoading: editingEvent }] = useEditEventMutation();

  const { data: eventResponse, isLoading: loadingEvent } = useGetEventQuery(
    {
      slug: slug,
    },
    { skip: mode === "create", refetchOnMountOrArgChange: true },
  );

  const { data: eventTicketsResponse, isLoading: loadingTickets } =
    useGetEventTicketsQuery(
      {
        slug: slug,
      },
      { skip: mode === "create", refetchOnMountOrArgChange: true },
    );

  const { data: guests } = useGetEventGuestsQuery(
    {
      id: eventResponse?.data?._id ?? "",
      search: "",
      page: 1,
      limit: 1,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: mode === "create" || !eventResponse?.data?._id,
    },
  );

  const doNoSaveExit = useCallback(() => {
    const event = eventResponse?.data;

    // Redirect to event details page on cancel only when you're editing an event that is not in draft
    if (event?._id && event?.status !== "draft") {
      if (modal === "confirmExitModal") {
        navigate(`/events/${encodeURIComponent(event.slug)}/details`, {
          replace: true,
        });
      } else {
        navigate(
          `/events/${encodeURIComponent(event.slug)}/details?tab=settings`,
          {
            replace: true,
          },
        );
      }
    } else if (event?._id && event?.status === "draft") {
      navigate("/events?EventStatus=draft", { replace: true });
    } else {
      navigate("/events", { replace: true });
    }
  }, [eventResponse, navigate, modal]);

  useEffect(() => {
    const now = new Date();
    const now1Hr = new Date();
    now1Hr.setHours(now1Hr.getHours() + 1);

    if (mode === "create") {
      const payload = {
        name: "",
        description: "",
        location: "",
        visibility: "public",
        category: "General",
        feePayer: "organiser",
        timezone: clientTZ,
        hideLocation: false,
        placeId: undefined,
        placeholderLocation: undefined,
        longitude: undefined,
        latitude: undefined,
        startDate: "",
        endDate: "",
        startTime: getRoundedTimeToNearestXMin(now, 15),
        endTime: getRoundedTimeToNearestXMin(now1Hr, 15),
        image: "",
        type: "physical",
        status: "upcoming",
        slug: undefined,
        tickets: [],
      } as CreateEvent;

      setCurrentPayload(payload);
      setFinalPayload(payload);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (mode === "edit" && eventResponse?.data && eventTicketsResponse?.data) {
      const payload = {
        name: eventResponse.data.name,
        description: eventResponse.data?.description ?? "",
        location: eventResponse.data.location,
        visibility: eventResponse.data?.visibility ?? "public",
        category: eventResponse.data?.category ?? "General",
        feePayer: eventResponse.data?.feePayer ?? "organiser",
        timezone: eventResponse.data?.timezone ?? clientTZ,
        hideLocation: eventResponse.data?.hideLocation ?? false,
        placeId: eventResponse.data?.placeId ?? undefined,
        placeholderLocation:
          eventResponse.data?.placeholderLocation ?? undefined,
        longitude: eventResponse.data?.longitude ?? undefined,
        latitude: eventResponse.data?.latitude ?? undefined,
        startDate: formatDate(eventResponse.data.startDate),
        endDate: formatDate(eventResponse.data.endDate),
        startTime: eventResponse.data.startTime,
        endTime: eventResponse.data.endTime,
        image: eventResponse.data.image,
        type: eventResponse.data.type,
        status: eventResponse.data.status,
        slug: eventResponse.data?.slug ?? "",
        tickets: eventTicketsResponse.data.tickets.map((ticket) => {
          return {
            ticketType: ticket.ticketType,
            name: ticket.name,
            price: ticket.price,
            description: ticket.description,
            quantityAvailable: ticket.quantityAvailable,
            quantity: ticket.quantity,
            quantityType: ticket.quantityType,
            currency: ticket.currency,
          };
        }),
      };

      setCurrentPayload(payload);
      setFinalPayload(payload);
    }
  }, [eventResponse, mode, eventTicketsResponse]);

  // Enforce profile completion before event creation
  useEffect(() => {
    if (mode === "create" && !hasCompletedProfile && !hasCreatedEvent) {
      navigate("/complete-profile", { replace: true });
    }
    // eslint-disable-next-line
  }, [hasCompletedProfile, mode, hasCreatedEvent]);

  // Confirmation before tab exit or reload
  useBeforeUnload((e) => {
    // handleModalOpen("confirmExitModal");
    e.preventDefault();
    e.returnValue = ""; // Required for Chrome
  });

  return (
    <Protected>
      <BareLayout
        title={showLivePreview ? "Preview Event" : `${mode} Event`}
        bodyClassName="flex-col gap-6 xl:!flex-row xl:!gap-0"
        cancelLoading={false}
        cancelText={"Cancel"}
        onCancel={async () => {
          const isDetailsEdited = !_.isEqual(currentPayload, finalPayload);

          if (isDetailsEdited) {
            handleModalOpen("confirmExitModal");
          } else {
            doNoSaveExit();
          }
        }}
      >
        {(mode === "create" && !showLivePreview) ||
        (mode === "edit" &&
          eventResponse?.data?.status === "draft" &&
          !showLivePreview) ? (
          <div className="top-[34px] hidden h-fit w-full max-w-[200px] flex-col md:sticky xl:flex">
            {steps.map((item) => {
              return (
                <div
                  key={item.id}
                  className={`flex items-center gap-2 p-3 text-sm font-bold ${step === item.id && "bg-white"} w-full rounded-2xl`}
                >
                  <Icon
                    svg="check"
                    width={20}
                    height={20}
                    className={`${step > item.id ? "text-success" : "text-grey-400"}`}
                  />
                  {item.title}
                </div>
              );
            })}
          </div>
        ) : undefined}

        {mode === "create" ||
        (mode === "edit" && eventResponse?.data?.status === "draft") ? (
          <div className="flex items-center justify-between gap-4 xl:hidden">
            <p className="text-sm font-bold text-grey-600">Step {step} of 3</p>

            <ProgressBar step={step} total={3} className="max-w-[100px]" />
          </div>
        ) : undefined}

        <div
          className={classNames("flex h-max flex-1 justify-center md:px-10", {
            "md:!px-0": showLivePreview,
          })}
        >
          <div
            className={classNames(`flex w-full flex-col gap-4`, {
              "max-w-[800px]": step === 3 && !showLivePreview,
              "max-w-[580px]": step !== 3 && !showLivePreview,
            })}
          >
            {loadingEvent || loadingTickets ? (
              <EventCardSkeleton showExtraDetails imageDimension={300} />
            ) : undefined}

            {step === 1 && finalPayload && !loadingEvent && !loadingTickets ? (
              <EventDetails
                finalPayload={finalPayload}
                currentPayload={currentPayload!}
                setCurrentPayload={
                  setCurrentPayload as React.Dispatch<
                    React.SetStateAction<CreateEvent>
                  >
                }
                setFinalPayload={
                  setFinalPayload as React.Dispatch<
                    React.SetStateAction<CreateEvent>
                  >
                }
                setStep={(s) => {
                  setStep(s);
                }}
                mode={mode}
                id={eventResponse?.data?._id ?? ""}
                ticketsBought={(guests?.data?.totalCount ?? 0) > 0}
                handleModalOpen={handleModalOpen}
              />
            ) : undefined}

            {step === 2 && finalPayload && !loadingEvent && !loadingTickets ? (
              <EventTickets
                finalPayload={finalPayload}
                currentPayload={currentPayload!}
                setFinalPayload={
                  setFinalPayload as React.Dispatch<
                    React.SetStateAction<CreateEvent>
                  >
                }
                setStep={(s) => {
                  setStep(s);
                }}
              />
            ) : undefined}

            {step === 3 && finalPayload && !loadingEvent && !loadingTickets ? (
              <EventPreview
                finalPayload={finalPayload}
                currentPayload={currentPayload!}
                setFinalPayload={
                  setFinalPayload as React.Dispatch<
                    React.SetStateAction<CreateEvent>
                  >
                }
                setStep={(s) => {
                  setStep(s);
                }}
                mode={mode}
                id={eventResponse?.data?._id ?? ""}
                showLivePreview={showLivePreview}
                setShowLivePreview={setShowLivePreview}
              />
            ) : undefined}
          </div>
        </div>
      </BareLayout>

      <ConfirmExitModal
        open={
          modalOpen &&
          ["confirmExitModal", "confirmGoToEventSettings"].includes(modal)
        }
        handleClose={() => {
          handleModalClose();
        }}
        loading={editingEvent || creatingEvent}
        onProceed={async () => {
          // If creating event
          if (mode === "create" && finalPayload) {
            const resp = await createEvent({
              ...finalPayload,
              status: "draft",
            });

            if (resp.data?.status === "success") {
              navigate(`/events?EventStatus=draft`, { replace: true });
            }
          }
          // If editing a draft event
          else if (mode === "edit" && finalPayload?.status === "draft") {
            const resp = await editEvent({
              id: eventResponse?.data?._id ?? "",
              payload: {
                ...finalPayload,
                status: "draft",
              },
            });

            if (resp.data?.status === "success") {
              navigate(`/events?EventStatus=draft`, { replace: true });
            }
          }
          // If editing any other event type
          else {
            const resp = await editEvent({
              id: eventResponse?.data?._id ?? "",
              payload: {
                ...finalPayload,
                tickets: undefined,
              },
            });

            if (resp.data?.status === "success") {
              if (modal === "confirmExitModal") {
                navigate(
                  `/events/${encodeURIComponent(resp.data.data.event.slug)}/details`,
                  {
                    replace: true,
                  },
                );
              } else {
                navigate(
                  `/events/${encodeURIComponent(resp.data.data.event.slug)}/details?tab=settings`,
                  { replace: true },
                );
              }
            }
          }
        }}
        onCancel={() => {
          doNoSaveExit();
        }}
        mode={
          mode === "create" ||
          (mode === "edit" && finalPayload?.status === "draft")
            ? "create"
            : "edit"
        }
      />
    </Protected>
  );
};
